<template>
	<div class="ai-assistant-page">
		<div class="ai-assistant-scroll">
			<section class="hero-section">
				<div class="section-shell hero-grid">
					<div class="hero-copy">
						<h1>让<em>AI</em>真正<br />在农企落地</h1>
						<p class="hero-description">写文案、做图片、做报表、拟合同、查行情、算损耗、<br />追欠款、设计营销方案等，小果都可以帮你做！</p>
						<div class="hero-download">
							<ai-download-menu class="download-menu" :downloads="desktopDownloads">
								<span class="download-button">下载森果云 AI 助手 <span aria-hidden="true">↓</span></span>
							</ai-download-menu>
						</div>
						<div class="hero-qrcode-grid" aria-label="AI 助手咨询与教程入口">
							<article class="hero-qrcode-item">
								<div class="hero-qrcode-image">
									<img :src="customerServiceQrCode" alt="森果云 AI 助手客服咨询二维码" @error="fallbackCustomerServiceQrCode" />
								</div>
								<h2>客服咨询</h2>
								<p>微信扫码添加</p>
							</article>
							<article class="hero-qrcode-item">
								<div class="hero-qrcode-image">
									<img :src="wecomInviteQrCode" alt="森果云农企 AI 落地实战群二维码" @error="fallbackWecomInviteQrCode" />
								</div>
								<h2>农企 AI 落地实战群</h2>
								<p>微信扫码进群</p>
							</article>
						</div>
					</div>
					<div class="hero-visual" aria-label="森果云 AI 助手桌面端界面截图">
						<div class="orbit-label"><i></i>本月经营一图看清</div>
						<div class="xiaoguo-card">
							<img src="@/assets/images/ai-assistant/xiaoguo.webp" alt="" />
							<span>有工作，<strong>找小果</strong></span>
						</div>
						<div class="report-window">
							<img :src="heroImage" alt="森果云 AI 助手生成本月经营健康报告的桌面界面" @error="fallbackHeroImage" />
						</div>
					</div>
				</div>
			</section>

			<section class="official-section official-section--white" id="query">
				<div class="section-shell official-grid">
					<div class="section-copy">
						<p class="section-index">连接森果云数据查询</p>
						<h2>全盘数据连起来<br /><em>一句话问清经营情况</em></h2>
						<p class="section-desc">登录店铺后，小果连接当前账号有权限查看的商品、批次、客户、销售和库存数据，按你的要求做报表、找原因、给建议。</p>
						<div class="point-list">
							<div v-for="point in queryPoints" :key="point.title" class="point">
								<i>{{ point.icon }}</i>
								<div>
									<b>{{ point.title }}</b>
									<p>{{ point.description }}</p>
								</div>
							</div>
						</div>
					</div>
					<div class="query-scene">
						<div class="query-question">“帮我看看这个月什么货更赚钱，哪些客户最值得维护？”</div>
						<div class="query-report">
							<div class="query-report-head"><span>本月经营分析 · 数据截至今日 09:30</span><em>● 已完成</em></div>
							<h3>利润结构变好，库存周转仍需关注</h3>
							<div class="query-report-grid">
								<div><span>高利润商品</span><strong>精品番茄</strong><small>毛利率 31.2%</small></div>
								<div><span>高价值客户</span><strong>兴旺餐饮</strong><small>本月贡献 +22%</small></div>
								<div><span>高利润批次</span><strong>脐橙 0826</strong><small>单批毛利 ¥8,620</small></div>
								<div><span>经营机会</span><strong>沉默客户召回</strong><small>预计可唤醒 6 位</small></div>
							</div>
							<div class="query-report-foot"><b>风险提醒</b>3 个批次周转速度下降，建议优先促销或调价。</div>
						</div>
						<img class="query-xiaoguo" src="@/assets/images/ai-assistant/xiaoguo.webp" alt="" />
						<div class="query-caption">数据我来串，重点你一眼看懂。</div>
						<div class="query-clue query-clue--profit"><i></i>已对比商品与批次利润</div>
						<div class="query-clue query-clue--customer"><i></i>已找到高价值客户</div>
					</div>
				</div>
			</section>

			<section class="official-section official-section--tint" id="entry">
				<div class="section-shell official-grid">
					<div class="section-copy">
						<p class="section-index">数据录入<span class="section-status">开发中</span></p>
						<h2>档案和单据免手输<br /><em>一句话、一张图全搞定</em></h2>
						<p class="section-desc">发语音，拍照片或截图，小果自动识别生成草稿，您只需一键确认即可录入系统。</p>
						<div class="point-list">
							<div v-for="point in entryPoints" :key="point.title" class="point">
								<i>{{ point.icon }}</i>
								<div>
									<b>{{ point.title }}</b>
									<p>{{ point.description }}</p>
								</div>
							</div>
						</div>
					</div>
					<div class="entry-stage">
						<div class="entry-tabs" role="tablist" aria-label="数据录入示例">
							<button
								v-for="(entry, key) in entryCases"
								:key="key"
								type="button"
								role="tab"
								:aria-selected="entryCase === key"
								:class="{ active: entryCase === key }"
								@click="entryCase = key"
							>
								{{ entry.label }}
							</button>
						</div>
						<div class="entry-source">
							<div class="entry-message">
								<i>{{ currentEntry.avatar }}</i>
								<div>
									<span>{{ currentEntry.sourceLabel }}</span>
									<p>{{ currentEntry.sourceText }}</p>
								</div>
							</div>
							<div class="entry-placeholder">{{ currentEntry.placeholder }}</div>
						</div>
						<div class="entry-bridge"><img src="@/assets/images/ai-assistant/xiaoguo.webp" alt="" /><span>已识别并匹配</span></div>
						<div class="entry-document" role="tabpanel">
							<div class="entry-document-head">
								<b>{{ currentEntry.title }}</b
								><span>等待确认</span>
							</div>
							<div class="entry-document-rows">
								<div v-for="row in currentEntry.rows" :key="row[0]">
									<span>{{ row[0] }}</span
									><b>{{ row[1] }}</b>
								</div>
							</div>
							<div class="entry-confirm">
								<small>{{ currentEntry.tip }}</small
								><button type="button">{{ currentEntry.button }}</button>
							</div>
						</div>
						<div class="entry-flow">
							<div v-for="(step, index) in currentEntry.steps" :key="step">
								<span>第 {{ index + 1 }} 步</span><b>{{ step }}</b>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section class="official-section official-section--white" id="custom">
				<div class="section-shell official-grid">
					<div class="section-copy">
						<p class="section-index">插件功能</p>
						<h2>想要自定义功能<br /><em>直接让小果做</em></h2>
						<p class="section-desc">过去要提需求、排计划、等开发；现在把想法说清楚，小果能快速生成新页面和新功能。</p>
						<div class="point-list">
							<div v-for="point in customPoints" :key="point.title" class="point">
								<i>{{ point.icon }}</i>
								<div>
									<b>{{ point.title }}</b>
									<p>{{ point.description }}</p>
								</div>
							</div>
						</div>
					</div>
					<div class="plugin-board" role="img" aria-label="向小果提出帮我在左侧加一个经营看板后生成经营看板的示意">
						<div class="plugin-demo" aria-hidden="true">
							<div class="plugin-float">
								<span class="plugin-float-icon">
									<svg viewBox="0 0 24 24" fill="none"><path d="M8 3v5M16 3v5M6 8h12v3a6 6 0 0 1-6 6v4M9 12h6" /></svg>
								</span>
								<p><strong>帮我在左侧加一个经营看板。</strong></p>
							</div>
							<svg class="plugin-route" viewBox="0 0 90 230" fill="none">
								<path d="M70 4C72 56 24 59 29 116s22 60 4 101" />
								<path d="m26 207 7 10 9-8" />
							</svg>
							<div class="plugin-app-frame">
								<img class="plugin-app-image" src="@/assets/images/ai-assistant/plugin-workspace.webp" alt="" />
								<div class="plugin-tab">
									<span
										><svg viewBox="0 0 24 24" fill="none"><path d="M5 19V9M12 19V5M19 19v-7M3 19h18" /></svg
									></span>
									<strong>经营看板</strong>
								</div>
								<div class="plugin-dashboard">
									<div class="plugin-dashboard-head">
										<div>
											<span
												><svg viewBox="0 0 24 24" fill="none"><path d="M5 19V9M12 19V5M19 19v-7M3 19h18" /></svg
											></span>
											<p><strong>经营看板</strong><em>由插件提供</em></p>
										</div>
										<b><i></i>数据已更新</b>
									</div>
									<div class="plugin-dashboard-summary"><span>本月经营概览</span><strong>销售稳步增长，经营整体向好</strong></div>
									<div class="plugin-dashboard-metrics">
										<div><span>销售额</span><strong>12.8万</strong><em>环比 +18%</em></div>
										<div><span>毛利率</span><strong>26.8%</strong><em>提升 1.9%</em></div>
										<div><span>新增客户</span><strong>86</strong><em>环比 +16%</em></div>
									</div>
									<div class="plugin-dashboard-lower">
										<div class="plugin-trend">
											<p><strong>近 6 个月销售趋势</strong><span>持续上升</span></p>
											<div>
												<i style="height: 38%"></i><i style="height: 50%"></i><i style="height: 61%"></i><i style="height: 69%"></i><i style="height: 82%"></i
												><i style="height: 94%"></i>
											</div>
										</div>
										<div class="plugin-next-action">
											<span>建议先做</span><strong>关注 3 项经营机会</strong>
											<p>高毛利品类、沉默客户、新客复购</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section class="market-section" id="market">
				<div class="section-shell market-layout">
					<div class="market-intro">
						<p class="section-index">插件生态</p>
						<h2>同行好用的插件<br /><em>你可一键安装</em></h2>
						<p>插件市场里既有森果云提供的能力，也有大家上传分享的经营插件。别人已经跑通的好功能，可以直接安装，再按自己的想法调整。</p>
						<div class="market-sign">
							<img src="@/assets/images/ai-assistant/xiaoguo.webp" alt="" />
							<div>
								<b>小果插件市场 <em>开发中</em></b
								><span>同行正在使用的经营好工具</span>
							</div>
						</div>
					</div>
					<div class="market-shelf">
						<article v-for="plugin in plugins" :key="plugin.title" class="market-plugin">
							<div class="market-plugin-head">
								<b>{{ plugin.title }}</b
								><small>{{ plugin.author }}</small>
							</div>
							<p>{{ plugin.description }}</p>
							<span class="plugin-status">开发中</span>
							<div class="plugin-illustration" :class="`plugin-illustration--${plugin.tone}`" aria-hidden="true">
								<svg v-if="plugin.tone === 'market'" viewBox="0 0 160 100" focusable="false">
									<rect class="illustration-panel" x="4" y="8" width="152" height="84" rx="12" />
									<rect class="illustration-accent" x="17" y="19" width="34" height="5" rx="2.5" />
									<rect class="illustration-line" x="112" y="20" width="28" height="4" rx="2" />
									<rect v-for="x in [17, 58, 99]" :key="x" class="illustration-soft" :x="x" y="31" width="36" height="18" rx="5" />
									<path class="illustration-muted-stroke" d="M18 78h68M21 74l15-10 13 4 15-12 19 5" />
									<circle class="illustration-accent" cx="64" cy="56" r="3.5" />
									<rect class="illustration-line" x="99" y="70" width="8" height="8" />
									<rect class="illustration-line" x="112" y="63" width="8" height="15" />
									<rect class="illustration-line" x="125" y="57" width="8" height="21" />
									<rect class="illustration-accent" x="138" y="49" width="8" height="29" />
								</svg>
								<svg v-else-if="plugin.tone === 'customer'" viewBox="0 0 160 100" focusable="false">
									<rect class="illustration-panel" x="4" y="8" width="152" height="84" rx="12" />
									<rect class="illustration-soft" x="14" y="25" width="40" height="50" rx="10" />
									<circle class="illustration-accent-stroke" cx="34" cy="42" r="8" />
									<path class="illustration-accent-stroke" d="M20 68c2-11 7-16 14-16s12 5 14 16" />
									<path class="illustration-dash" d="M57 50h14m-5-5 5 5-5 5" />
									<rect class="illustration-panel" x="76" y="21" width="45" height="58" rx="10" />
									<path class="illustration-accent-stroke" d="M91 52h15m-13-1c0-7 2-11 6-11s7 4 7 11l4 5H89Zm4 6c1 4 5 4 7 0" />
									<path class="illustration-dash" d="M124 50h10m-5-5 5 5-5 5" />
									<circle class="illustration-panel" cx="143" cy="50" r="10" />
									<path class="illustration-accent-stroke" d="m138 50 4 4 7-9" />
								</svg>
								<svg v-else-if="plugin.tone === 'inventory'" viewBox="0 0 160 100" focusable="false">
									<rect class="illustration-panel" x="4" y="8" width="152" height="84" rx="12" />
									<rect v-for="x in [17, 61, 105]" :key="x" class="illustration-soft" :x="x" y="20" width="37" height="18" rx="5" />
									<path class="illustration-muted-stroke" d="M17 78h125" />
									<rect class="illustration-line" x="24" y="62" width="14" height="16" rx="2" />
									<rect class="illustration-line" x="46" y="54" width="14" height="24" rx="2" />
									<rect class="illustration-line" x="68" y="47" width="14" height="31" rx="2" />
									<rect class="illustration-accent" x="90" y="39" width="14" height="39" rx="2" />
									<circle class="illustration-panel" cx="126" cy="58" r="17" />
									<path class="illustration-accent-stroke" d="M126 47v12l8 5" />
									<circle class="illustration-accent" cx="126" cy="58" r="2.5" />
								</svg>
								<svg v-else viewBox="0 0 160 100" focusable="false">
									<rect class="illustration-panel" x="4" y="8" width="152" height="84" rx="12" />
									<rect class="illustration-soft" x="14" y="20" width="52" height="60" rx="8" />
									<rect class="illustration-soft" x="94" y="20" width="52" height="60" rx="8" />
									<rect class="illustration-line" x="23" y="31" width="27" height="4" rx="2" />
									<rect class="illustration-line" x="23" y="43" width="34" height="3" rx="1.5" />
									<rect class="illustration-accent" x="23" y="55" width="25" height="4" rx="2" />
									<rect class="illustration-line" x="103" y="31" width="27" height="4" rx="2" />
									<rect class="illustration-line" x="103" y="43" width="34" height="3" rx="1.5" />
									<rect class="illustration-accent" x="103" y="55" width="25" height="4" rx="2" />
									<path class="illustration-dash" d="M69 39h20m-6-5 6 5-6 5M91 60H71m6-5-6 5 6 5" />
									<circle class="illustration-panel" cx="80" cy="76" r="10" />
									<path class="illustration-accent-stroke" d="m75 76 4 4 7-9" />
								</svg>
							</div>
						</article>
					</div>
				</div>
			</section>

			<section class="general-section" id="general">
				<div class="section-shell general-layout">
					<div class="general-intro">
						<p class="section-index">通用工作能力</p>
						<h2>这些工作<br /><em>小果也能做</em></h2>
						<p>写文案、做图片、拟/审合同、做营销方案和报表，还能联通钉钉、企业微信和飞书。</p>
					</div>
					<div class="ability-map" aria-label="小果通用 AI 能力图">
						<svg class="ability-lines" viewBox="0 0 700 400" preserveAspectRatio="none" aria-hidden="true">
							<defs>
								<linearGradient id="ability-line-gradient" x1="0" y1="0" x2="1" y2="1">
									<stop offset="0" stop-color="#0aadf5" />
									<stop offset="1" stop-color="#8656fa" />
								</linearGradient>
							</defs>
							<g stroke="url(#ability-line-gradient)">
								<path d="M350 200 C290 140 220 88 92 36" />
								<path d="M350 200 C410 140 480 88 608 36" />
								<path d="M350 200 C270 200 190 200 76 200" />
								<path d="M350 200 C430 200 510 200 624 200" />
								<path d="M350 200 C290 264 215 316 110 364" />
								<path d="M350 200 C410 264 485 316 590 364" />
							</g>
							<circle cx="92" cy="36" r="5" />
							<circle cx="608" cy="36" r="5" />
							<circle cx="76" cy="200" r="5" />
							<circle cx="624" cy="200" r="5" />
							<circle cx="110" cy="364" r="5" />
							<circle cx="590" cy="364" r="5" />
						</svg>
						<div class="ability-orb">
							<img src="@/assets/images/ai-assistant/xiaoguo.webp" alt="小果 AI 助手" /><span><i></i>小果能力中心</span>
						</div>
						<article v-for="capability in capabilities" :key="capability.title" class="ability-node" :class="capability.position">
							<i>{{ capability.icon }}</i>
							<div>
								<h3>{{ capability.title }}</h3>
								<p v-if="capability.title !== '联通办公系统'">{{ capability.description }}</p>
								<div v-else class="system-badges"><span>钉钉</span><span>企业微信</span><span>飞书</span></div>
							</div>
						</article>
					</div>
				</div>
			</section>

			<section class="official-section official-section--tint" id="permission">
				<div class="section-shell official-grid">
					<div class="section-copy">
						<p class="section-index">数据权限</p>
						<h2>AI 可以灵活<br /><em>数据边界不能含糊</em></h2>
						<p class="section-desc">小果不是把整店数据打包交出去，而是按当前账号权限和本次任务取用必要信息；遇到写入操作，还要经过人的确认。</p>
						<div class="point-list">
							<div v-for="point in securityItems" :key="point.title" class="point">
								<i>{{ point.icon }}</i>
								<div>
									<b>{{ point.title }}</b>
									<p>{{ point.description }}</p>
								</div>
							</div>
						</div>
					</div>
					<div class="permission-stage">
						<article class="permission-ticket permission-ticket--top">
							<span>只读取当前账号能看的</span>
							<h3>查看近 7 天销售趋势</h3>
							<p>使用当前店铺的销售汇总数据，不会查询其他店铺。</p>
							<footer><span>武汉白沙洲店 · 管理员</span><b>允许查看</b></footer>
						</article>
						<article class="permission-ticket permission-ticket--bottom">
							<span>写入需要人工确认</span>
							<h3>生成销售开票单草稿</h3>
							<p>小果先整理客户、商品和数量，确认无误后才正式写入系统。</p>
							<footer><span>草稿已生成 · 尚未写入</span><b>等待确认</b></footer>
						</article>
					</div>
				</div>
			</section>

			<section class="consult-section" id="consult">
				<div class="section-shell consult-layout">
					<div class="consult-copy">
						<span>AI 实施服务</span>
						<h2>不知道 AI 怎么用<br /><em>我们来帮你</em></h2>
						<p>先从每天最费时间的一件事开始。小果先做出能用的一版，陪你放进真实工作里试，再根据反馈一点点调成你的工作方式。</p>
						<button class="consult-button" type="button" @click="openConsult">免费咨询<i class="el-icon-right"></i></button>
					</div>
					<div class="consult-scene">
						<div class="consult-question"><small>不用先想清楚怎么用 AI</small><b>先告诉小果，你每天最头疼的是哪件事。</b></div>
						<div class="consult-avatar"><img src="@/assets/images/ai-assistant/xiaoguo.webp" alt="小果 AI 助手" /></div>
						<div class="consult-companion"><i></i>小果陪你边做边调</div>
						<div v-for="step in serviceSteps" :key="step.number" class="consult-step" :class="step.position">
							<i>{{ step.number }}</i>
							<div>
								<b>{{ step.title }}</b
								><small>{{ step.description }}</small>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section class="cta-section">
				<div class="section-shell cta-card">
					<div class="cta-copy">
						<h2>用 AI 重塑经营效率，从今天开始</h2>
						<p>查数据、算利润、做分析，随时随地帮你解决繁琐问题。</p>
					</div>
					<img src="@/assets/images/ai-assistant/cta-business-clarity.webp" alt="" />
					<ai-download-menu class="cta-download" :downloads="desktopDownloads" placement="above">
						<span class="cta-download-button">下载森果云 AI 助手<i class="el-icon-bottom"></i></span>
					</ai-download-menu>
				</div>
			</section>
		</div>

		<div v-if="consultVisible" class="consult-mask" role="presentation" @click.self="closeConsult">
			<div class="consult-dialog" role="dialog" aria-modal="true" aria-labelledby="consult-title">
				<button ref="closeButton" class="dialog-close" type="button" aria-label="关闭咨询窗口" @click="closeConsult"><i class="el-icon-close"></i></button>
				<div class="dialog-brand"><img src="@/assets/images/ai-assistant/xiaoguo.webp" alt="" /><span>森果云 AI 助手</span></div>
				<h2 id="consult-title">现在咨询</h2>
				<p>微信扫码，告诉我们你最想用 AI 解决什么问题。</p>
					<div class="qrcode-frame"><img :src="customerServiceQrCode" alt="森果云 AI 助手咨询二维码" @error="fallbackCustomerServiceQrCode" /></div>
				<span class="dialog-tip">扫描二维码添加咨询</span>
			</div>
		</div>
	</div>
</template>

<script>
import customerServiceQrCodeFallback from '@/assets/images/ai-assistant/ai-wechat-qrcode-v3.png'
import heroImageFallback from '@/assets/images/ai-assistant/workspace-report-v2.webp'
import wecomInviteQrCodeFallback from '@/assets/images/ai-assistant/ai-tutorial-group-qrcode.png'
import AiDownloadMenu from './download-menu.vue'
import { getAssistantPromotionImages } from './promotion'

const AI_DESKTOP_DOWNLOADS = Object.freeze({
	mac: 'https://ai-platform-api.senguo.cc/api/v1/desktop-releases/latest?platform=mac',
	windows: 'https://ai-platform-api.senguo.cc/api/v1/desktop-releases/latest?platform=windows',
})

export default {
	name: 'AiAssistant',
	components: {
		AiDownloadMenu,
	},
	data() {
		return {
			desktopDownloads: AI_DESKTOP_DOWNLOADS,
			heroImage: heroImageFallback,
			customerServiceQrCode: customerServiceQrCodeFallback,
			wecomInviteQrCode: wecomInviteQrCodeFallback,
			promotionRequestActive: true,
			consultVisible: false,
			consultTrigger: null,
			bodyOverflowBeforeConsult: null,
			entryCase: 'profile',
			entryCases: {
				profile: {
					label: '档案录入',
					avatar: '档',
					sourceLabel: '来源 · 新档案资料',
					sourceText: '新增兴旺餐饮二店会员，并录入精品番茄、青椒两种商品资料。',
					placeholder: '会员资料 / 商品表格也能识别',
					steps: ['识别档案内容', '查找已有资料', '生成档案草稿', '等待人工确认'],
					title: '档案录入草稿',
					rows: [
						['会员录入', '兴旺餐饮二店 · 李经理'],
						['商品录入', '精品番茄 · 青椒'],
						['录入结果', '2 类档案待确认'],
					],
					tip: '核对会员和商品信息后才会写入系统',
					button: '确认录入档案',
				},
				document: {
					label: '单据录入',
					avatar: '单',
					sourceLabel: '来源 · 群消息和采购清单',
					sourceText: '兴旺餐饮明早要货 30 件；陈老板脐橙 0828 批次入库 80 件。',
					placeholder: '聊天图片 / 采购清单也能识别',
					steps: ['识别单据信息', '匹配往来单位商品', '生成单据草稿', '等待人工确认'],
					title: '单据录入草稿',
					rows: [
						['开票单', '兴旺餐饮二店 · 30 件'],
						['入库单', '脐橙 0828 · 80 件'],
						['单据状态', '2 张单据待确认'],
					],
					tip: '核对开票和入库信息后才会写入系统',
					button: '确认录入单据',
				},
			},
			queryPoints: [
				{ icon: '✓', title: '哪些商品、哪些批次利润更高', description: '把售价、成本和损耗放在一起看，找到真正赚钱的货。' },
				{ icon: '✓', title: '哪些客户更值得重点维护', description: '综合贡献、复购、活跃度与欠款，看清长期价值。' },
				{ icon: '✓', title: '哪些经营环节有风险', description: '库存积压、毛利异常、客户沉默等变化及时提醒。' },
			],
			entryPoints: [
				{ icon: '档', title: '基础建档', description: '商品、会员、供货商资料批量录入，免去手动逐个填写。' },
				{ icon: '单', title: '单据识别', description: '微信消息截图、手写单等自动识别，秒级生成单据草稿。' },
				{ icon: '人', title: '核对再入账', description: '草稿确认无误再同步进系统，账目数据绝对精准可控。' },
			],
			customPoints: [
				{ icon: '说', title: '一句话描述想法', description: '不用画原型，也不用懂技术，说清楚经营上想解决什么。' },
				{ icon: '看', title: '立刻看到可用页面', description: '新看板、新入口和新流程先生成效果，边看边调。' },
				{ icon: '改', title: '继续用人话调整', description: '哪里不顺手就接着说，字号、字段和规则都能继续改。' },
			],
			plugins: [
				{ title: '果蔬行情分析', author: '产地行情观察室 · 4.9 分', description: '汇总公开行情与本店进销价，生成趋势判断和进货建议。', tone: 'market' },
				{ title: '沉默客户唤醒', author: '武汉果批老周 · 4.8 分', description: '找到近期没来的老客户，自动整理回访名单和沟通话术。', tone: 'customer' },
				{ title: '滞销库存预警', author: '森果云官方 · 4.9 分', description: '结合库存天数和销量变化，提前发现批次积压风险。', tone: 'inventory' },
				{ title: '供应商对账助手', author: '长沙联营档口 · 4.7 分', description: '按供应商汇总入库、退货和付款记录，快速核对差异。', tone: 'reconcile' },
			],
			capabilities: [
				{ title: '做营销方案', description: '从活动主题到客户触达话术', icon: '营', position: 'ability-node--marketing' },
				{ title: '做图片', description: '商品宣传图、活动海报和配图', icon: '图', position: 'ability-node--image' },
				{ title: '拟 / 审合同', description: '起草、审阅合作条款，生成修改建议', icon: '合', position: 'ability-node--contract' },
				{ title: '联通办公系统', description: '钉钉、企业微信和飞书协作', icon: '联', position: 'ability-node--systems' },
				{ title: '数据分析', description: '找变化、看原因，给出经营建议', icon: '析', position: 'ability-node--analysis' },
				{ title: '做报表', description: '整理数据，生成图表、结论和摘要', icon: '表', position: 'ability-node--report' },
			],
			securityItems: [
				{ icon: '店', title: '业务记录仍在森果云', description: '订单、库存、客户等业务记录继续由森果云系统保存和管理。' },
				{ icon: '权', title: '只读取当前账号有权限的数据', description: '店铺、岗位和账号原有的权限边界继续生效。' },
				{ icon: '确', title: '写入操作需要人确认', description: '开票、入库等操作先生成草稿，确认后才正式执行。' },
				{ icon: '本', title: '软件本地运行，数据留在本地', description: 'AI 助手在您的电脑上运行，处理过程中的数据留在本地，不会把整店数据打包上传。' },
			],
			serviceSteps: [
				{ number: '01', title: '先做出能用的一版', description: '小果理解问题，搭好第一版流程', position: 'consult-step--one' },
				{ number: '02', title: '放进真实工作里试', description: '员工照常做事，小果在旁协助', position: 'consult-step--two' },
				{ number: '03', title: '边用边调成自己的', description: '哪里不顺就继续说，越用越顺手', position: 'consult-step--three' },
			],
		}
	},
	mounted() {
		this.loadAssistantPromotion()
	},
	computed: {
		currentEntry() {
			return this.entryCases[this.entryCase]
		},
	},
	beforeDestroy() {
		this.promotionRequestActive = false
		this.cleanupConsult()
	},
	methods: {
		loadAssistantPromotion() {
			getAssistantPromotionImages('retail_wecom_invite_qr_code')
				.then(images => {
					if (!this.promotionRequestActive) return
					if (images.heroImage) this.heroImage = images.heroImage
					if (images.customerServiceQrCode) this.customerServiceQrCode = images.customerServiceQrCode
					if (images.wecomInviteQrCode) this.wecomInviteQrCode = images.wecomInviteQrCode
				})
				.catch(() => undefined)
		},
		fallbackHeroImage() {
			this.heroImage = heroImageFallback
		},
		fallbackCustomerServiceQrCode() {
			this.customerServiceQrCode = customerServiceQrCodeFallback
		},
		fallbackWecomInviteQrCode() {
			this.wecomInviteQrCode = wecomInviteQrCodeFallback
		},
		openConsult(event) {
			if (this.consultVisible) return
			this.consultTrigger = event && event.currentTarget
			this.consultVisible = true
			this.bodyOverflowBeforeConsult = document.body.style.overflow
			document.body.style.overflow = 'hidden'
			document.addEventListener('keydown', this.handleConsultKeydown)
			this.$nextTick(() => {
				if (this.$refs.closeButton) this.$refs.closeButton.focus()
			})
		},
		closeConsult() {
			if (!this.consultVisible) return
			this.consultVisible = false
			this.cleanupConsult()
			this.$nextTick(() => {
				if (this.consultTrigger && typeof this.consultTrigger.focus === 'function') this.consultTrigger.focus()
				this.consultTrigger = null
			})
		},
		handleConsultKeydown(event) {
			if (event.key === 'Escape') this.closeConsult()
			if (event.key === 'Tab' && this.$refs.closeButton) {
				event.preventDefault()
				this.$refs.closeButton.focus()
			}
		},
		cleanupConsult() {
			if (this.bodyOverflowBeforeConsult !== null) {
				document.body.style.overflow = this.bodyOverflowBeforeConsult
				this.bodyOverflowBeforeConsult = null
			}
			document.removeEventListener('keydown', this.handleConsultKeydown)
		},
	},
}
</script>

<style lang="scss" scoped>
$aiBlue: #0aadf5;
$aiPurple: #8656fa;

.ai-assistant-page {
	--official-ai-blue: #0aadf5;
	--official-ai-purple: #8656fa;
	--official-ai-purple-deep: #6031cf;
	--official-green: #1bb783;
	--official-orange: #ff6a2b;
	--official-ink: #211d2d;
	--official-ink-soft: #676173;
	--official-muted: #918b9b;
	position: relative;
	width: 100%;
	height: 100%;
	min-height: 0;
	background: #ffffff;
	color: var(--official-ink);
	text-align: left;
}

.ai-assistant-page::before {
	position: absolute;
	z-index: 2;
	top: 0;
	left: 0;
	width: 260px;
	height: 320px;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.5) 30%, rgba(255, 255, 255, 0) 58%);
	clip-path: polygon(0 0, 100% 0, 0 100%);
	content: '';
	pointer-events: none;
}

.ai-assistant-scroll {
	height: 100%;
	overflow-x: hidden;
	overflow-y: auto;
	background: #ffffff;
}

.section-shell {
	width: calc(100% - 64px);
	max-width: 1240px;
	margin: 0 auto;
}

.hero-section {
	position: relative;
	overflow: hidden;
	box-sizing: border-box;
	padding: 104px 0 88px;
	background:
		radial-gradient(circle at 82% 18%, rgba(134, 86, 250, 0.17), transparent 29%), radial-gradient(circle at 8% 24%, rgba(10, 173, 245, 0.15), transparent 31%),
		linear-gradient(135deg, #eef9ff 0%, #fafcff 50%, #f7f1ff 100%);
}

.hero-section::before {
	position: absolute;
	inset: 0;
	background-image: linear-gradient(rgba(92, 64, 185, 0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(92, 64, 185, 0.07) 1px, transparent 1px);
	background-size: 44px 44px;
	content: '';
	mask-image: linear-gradient(90deg, #000000, rgba(0, 0, 0, 0.72) 63%, transparent 100%);
	opacity: 0.52;
	pointer-events: none;
}

.hero-section::after {
	position: absolute;
	bottom: -92px;
	left: 3vw;
	color: rgba(101, 63, 211, 0.035);
	content: 'AI';
	font-size: 300px;
	font-weight: 900;
	letter-spacing: -0.12em;
	line-height: 1;
	pointer-events: none;
}

.hero-grid {
	position: relative;
	z-index: 1;
	display: grid;
	grid-template-columns: minmax(430px, 0.84fr) minmax(560px, 1.16fr);
	gap: 52px;
	align-items: center;
}

.hero-copy {
	padding-bottom: 18px;
}

.hero-copy h1,
.cta-copy h2 {
	margin: 0;
	font-weight: 700;
	letter-spacing: -1.5px;
}

.hero-copy h1 {
	max-width: 590px;
	color: #1d1927;
	font-size: clamp(58px, 5vw, 78px);
	font-weight: 900;
	letter-spacing: -0.065em;
	line-height: 1.04;
	text-align: left;
}

.hero-copy h1 em {
	position: relative;
	display: inline-block;
	margin: 0 0.015em;
	background: linear-gradient(100deg, #0aadf5, #8656fa);
	-webkit-background-clip: text;
	background-clip: text;
	color: transparent;
	font-size: 1.23em;
	font-style: normal;
	font-weight: 900;
	letter-spacing: -0.085em;
	line-height: 0.82;
	transform: translateY(0.025em);
	-webkit-text-fill-color: transparent;
}

.hero-copy h1 em::after {
	position: absolute;
	z-index: -1;
	right: -0.09em;
	bottom: -0.13em;
	left: -0.04em;
	height: 0.14em;
	background: linear-gradient(90deg, rgba(10, 173, 245, 0.08), rgba(134, 86, 250, 0.26) 66%, rgba(134, 86, 250, 0.02));
	clip-path: polygon(0 68%, 9% 43%, 29% 30%, 52% 38%, 76% 15%, 100% 24%, 93% 55%, 71% 51%, 49% 68%, 24% 60%, 4% 86%);
	content: '';
	transform: rotate(-2.5deg) skewX(-14deg);
	transform-origin: left center;
}

.hero-description {
	max-width: 570px;
	margin: 25px 0 0;
	color: #5f596a;
	font-size: 18px;
	line-height: 1.82;
	text-align: left;
}

.hero-download {
	margin-top: 30px;
}

.download-menu {
	position: relative;
	width: max-content;
}

.download-button {
	display: inline-flex;
	min-height: 52px;
	align-items: center;
	justify-content: center;
	gap: 12px;
	box-sizing: border-box;
	padding: 0 25px;
	border-radius: 13px;
	background: linear-gradient(100deg, $aiBlue, $aiPurple);
	box-shadow: 0 15px 30px rgba($aiPurple, 0.25);
	color: #ffffff;
	cursor: pointer;
	font-size: 16px;
	font-weight: 700;
	transition:
		transform 0.18s ease,
		box-shadow 0.18s ease;
}

.download-button:hover {
	box-shadow: 0 18px 36px rgba($aiPurple, 0.34);
	transform: translateY(-2px);
}

.download-button:focus-visible {
	outline: 3px solid rgba($aiBlue, 0.3);
	outline-offset: 3px;
}

.hero-qrcode-grid {
	display: grid;
	width: max-content;
	max-width: 100%;
	grid-template-columns: repeat(2, 116px);
	gap: 54px;
	margin-top: 20px;
}

.hero-qrcode-item {
	display: flex;
	min-width: 0;
	align-items: center;
	flex-direction: column;
	padding: 16px 0 0;
	background: transparent;
}

.hero-qrcode-image,
.hero-qrcode-placeholder {
	width: 116px;
	height: 116px;
	box-sizing: border-box;
	border-radius: 13px;
}

.hero-qrcode-image {
	overflow: hidden;
	padding: 5px;
	border: 1px solid #ece7f2;
	background: #ffffff;
}

.hero-qrcode-image img {
	display: block;
	width: 100%;
	height: 100%;
	border-radius: 8px;
	object-fit: cover;
}

.hero-qrcode-placeholder {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	border: 1px dashed rgba($aiPurple, 0.55);
	background: linear-gradient(145deg, #f1fbff, #f3edff);
	color: #6542c7;
}

.hero-qrcode-placeholder strong {
	font-size: 14px;
}

.hero-qrcode-placeholder span {
	margin-top: 4px;
	font-size: 12px;
}

.hero-qrcode-item h2 {
	margin: 13px 0 0;
	color: #2a2436;
	font-size: 18px;
	font-weight: 500;
	line-height: 1.4;
	text-align: center;
	white-space: nowrap;
}

.hero-qrcode-item p {
	margin: 5px 0 0;
	color: #756e7c;
	font-size: 13px;
	line-height: 1.5;
	text-align: center;
	white-space: nowrap;
}

.consult-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 13px;
	margin-top: 34px;
	padding: 15px 24px;
	border: 0;
	border-radius: 10px;
	background: linear-gradient(100deg, $aiBlue, $aiPurple);
	box-shadow: 0 12px 28px rgba($aiPurple, 0.24);
	color: #ffffff;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease,
		background 0.2s ease;
}

.consult-button:hover {
	transform: translateY(-2px);
	background: linear-gradient(100deg, $aiPurple, $aiBlue);
	box-shadow: 0 15px 34px rgba($aiPurple, 0.32);
}

.hero-visual {
	position: relative;
	min-width: 0;
	perspective: 1500px;
}

.hero-visual::before {
	position: absolute;
	inset: 9% 2% 3% 12%;
	border-radius: 50%;
	background: radial-gradient(circle, rgba(89, 103, 247, 0.18), transparent 68%);
	content: '';
	filter: blur(16px);
}

.report-window {
	position: relative;
	overflow: hidden;
	border: 1px solid rgba(100, 73, 186, 0.2);
	border-radius: 24px;
	background: #ffffff;
	box-shadow:
		0 34px 85px rgba(54, 43, 90, 0.2),
		0 0 0 1px rgba(255, 255, 255, 0.72);
	transform: rotateX(1deg) rotateY(-2.5deg);
	transform-origin: center;
	transition:
		transform 0.42s cubic-bezier(0.2, 0.72, 0.2, 1),
		box-shadow 0.42s ease;
}

.report-window img {
	display: block;
	width: 100%;
}

.hero-visual:hover .report-window {
	box-shadow:
		0 42px 100px rgba(54, 43, 90, 0.25),
		0 0 0 1px rgba(255, 255, 255, 0.8);
	transform: rotateX(0.4deg) rotateY(-1deg) translateY(-2px);
}

.orbit-label {
	position: absolute;
	z-index: 4;
	top: -22px;
	right: 25px;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 13px;
	border: 1px solid rgba(112, 64, 224, 0.16);
	border-radius: 13px;
	background: rgba(255, 255, 255, 0.92);
	box-shadow: 0 14px 35px rgba(63, 48, 114, 0.14);
	color: #373042;
	font-size: 13px;
	font-weight: 700;
}

.orbit-label i {
	width: 9px;
	height: 9px;
	border-radius: 50%;
	background: #8656fa;
	box-shadow: 0 0 0 5px rgba(134, 86, 250, 0.12);
}

.xiaoguo-card {
	position: absolute;
	z-index: 4;
	bottom: 55px;
	left: -34px;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 5px 13px 5px 6px;
	border: 1px solid rgba(112, 64, 224, 0.16);
	border-radius: 13px;
	background: rgba(255, 255, 255, 0.92);
	box-shadow: 0 14px 35px rgba(63, 48, 114, 0.14);
}

.xiaoguo-card img {
	width: 48px;
	height: 48px;
	object-fit: contain;
}

.xiaoguo-card span {
	color: #393244;
	font-size: 14px;
	font-weight: 700;
	line-height: 1.25;
	white-space: nowrap;
}

.xiaoguo-card strong {
	color: #7040e0;
	font-weight: 900;
}

.official-section {
	position: relative;
	overflow: hidden;
	padding: 112px 0;
}

.official-section--white {
	background: #ffffff;
}

.official-section--tint {
	background: linear-gradient(135deg, #f5fbff 0%, #fbf9ff 64%, #f1ecff 100%);
}

.official-grid {
	display: grid;
	grid-template-columns: 0.82fr 1.18fr;
	gap: 72px;
	align-items: center;
}

.section-index {
	display: flex;
	align-items: center;
	gap: 10px;
	margin: 0 0 18px;
	color: var(--official-ai-purple-deep);
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.08em;
}

.section-index::before {
	width: 25px;
	height: 2px;
	background: linear-gradient(90deg, var(--official-ai-blue), var(--official-ai-purple));
	content: '';
}

.section-status {
	padding: 3px 8px;
	border-radius: 999px;
	background: #eee8ff;
	color: var(--official-ai-purple-deep);
	font-size: 11px;
	letter-spacing: 0;
}

.section-copy h2,
.market-intro h2,
.general-intro h2,
.consult-copy h2 {
	margin: 0;
	font-size: clamp(40px, 4vw, 56px);
	font-weight: 900;
	letter-spacing: -0.055em;
	line-height: 1.16;
}

.section-copy h2 em,
.market-intro h2 em,
.general-intro h2 em,
.consult-copy h2 em {
	color: var(--official-ai-purple-deep);
	font-style: normal;
}

.section-desc {
	max-width: 550px;
	margin: 20px 0 0;
	color: var(--official-ink-soft);
	font-size: 16px;
	line-height: 1.8;
}

.point-list {
	display: grid;
	gap: 18px;
	margin-top: 30px;
}

.point {
	display: grid;
	grid-template-columns: 28px 1fr;
	gap: 12px;
}

.point > i {
	display: grid;
	width: 24px;
	height: 24px;
	place-items: center;
	border-radius: 7px;
	background: #eee8ff;
	color: var(--official-ai-purple-deep);
	font-size: 12px;
	font-style: normal;
	font-weight: 900;
}

.point b {
	display: block;
	font-size: 16px;
}

.point p {
	margin: 5px 0 0;
	color: var(--official-muted);
	font-size: 12px;
	line-height: 1.65;
}

.query-scene,
.entry-stage,
.permission-stage {
	position: relative;
	min-height: 560px;
}

.query-scene::before,
.entry-stage::before,
.permission-stage::before {
	position: absolute;
	inset: 6% 2% 3%;
	border-radius: 48%;
	background: radial-gradient(circle, rgba(106, 81, 228, 0.16), rgba(10, 173, 245, 0.06) 48%, transparent 72%);
	content: '';
	filter: blur(8px);
}

.query-question {
	position: absolute;
	z-index: 4;
	top: 34px;
	left: 0;
	max-width: 330px;
	padding: 15px 17px;
	border: 1px solid rgba(112, 64, 224, 0.18);
	border-radius: 18px 6px 18px 18px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 18px 42px rgba(58, 44, 96, 0.16);
	color: #302a3a;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.6;
	transform: rotate(-2deg);
}

.query-report {
	position: absolute;
	z-index: 2;
	top: 68px;
	right: 0;
	width: 79%;
	box-sizing: border-box;
	padding: 27px;
	border: 1px solid rgba(112, 64, 224, 0.14);
	border-radius: 24px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 30px 80px rgba(58, 44, 101, 0.16);
	transform: rotate(1.2deg);
}

.query-report-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 18px;
}

.query-report-head span {
	color: var(--official-ai-purple-deep);
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.05em;
}

.query-report-head em {
	color: var(--official-green);
	font-size: 12px;
	font-style: normal;
}

.query-report h3 {
	margin: 13px 0 18px;
	font-size: 21px;
	line-height: 1.4;
}

.query-report-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 11px;
}

.query-report-grid div {
	padding: 14px;
	border-radius: 13px;
	background: #f7f6fa;
}

.query-report-grid span,
.query-report-grid strong,
.query-report-grid small {
	display: block;
}

.query-report-grid span {
	color: #918b98;
	font-size: 12px;
}

.query-report-grid strong {
	margin-top: 6px;
	font-size: 16px;
}

.query-report-grid small {
	margin-top: 4px;
	color: var(--official-green);
	font-size: 12px;
}

.query-report-foot {
	display: flex;
	align-items: center;
	gap: 9px;
	margin-top: 13px;
	padding: 12px 14px;
	border-left: 3px solid var(--official-orange);
	border-radius: 9px;
	background: #fff7f2;
	color: #785546;
	font-size: 12px;
	line-height: 1.6;
}

.query-xiaoguo {
	position: absolute;
	z-index: 4;
	bottom: 24px;
	left: 8px;
	width: 150px;
	height: auto;
	filter: drop-shadow(0 18px 28px rgba(48, 38, 81, 0.16));
}

.query-caption {
	position: absolute;
	z-index: 5;
	bottom: 56px;
	left: 116px;
	padding: 11px 14px;
	border: 1px solid rgba(112, 64, 224, 0.14);
	border-radius: 6px 15px 15px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 14px 30px rgba(58, 43, 99, 0.12);
	color: #5c5269;
	font-size: 12px;
	font-weight: 700;
}

.query-clue {
	position: absolute;
	z-index: 6;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 13px;
	border: 1px solid rgba(112, 64, 224, 0.13);
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.95);
	box-shadow: 0 12px 28px rgba(57, 43, 98, 0.11);
	color: #5b5364;
	font-size: 12px;
	font-weight: 800;
}

.query-clue i {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: var(--official-ai-purple);
	box-shadow: 0 0 0 5px rgba(134, 86, 250, 0.1);
}

.query-clue--profit {
	top: 37px;
	right: -15px;
}

.query-clue--customer {
	right: -20px;
	bottom: 34px;
}

.entry-stage {
	min-height: 510px;
}

.entry-tabs {
	position: absolute;
	z-index: 8;
	top: 4px;
	left: 50%;
	display: flex;
	gap: 8px;
	padding: 6px;
	border: 1px solid rgba(112, 64, 224, 0.14);
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.9);
	box-shadow: 0 14px 34px rgba(58, 43, 99, 0.11);
	transform: translateX(-50%);
}

.entry-tabs button {
	min-height: 38px;
	padding: 0 15px;
	border: 0;
	border-radius: 9px;
	background: transparent;
	color: #746c7c;
	font-size: 12px;
	font-weight: 800;
	cursor: pointer;
}

.entry-tabs button.active {
	background: linear-gradient(100deg, var(--official-ai-blue), var(--official-ai-purple));
	box-shadow: 0 8px 18px rgba(112, 64, 224, 0.23);
	color: #ffffff;
}

.entry-tabs button:focus-visible {
	outline: 3px solid rgba(10, 173, 245, 0.3);
	outline-offset: 3px;
}

.entry-source {
	position: absolute;
	z-index: 3;
	top: 112px;
	left: 0;
	width: 42%;
	box-sizing: border-box;
	padding: 22px 18px 25px;
	border: 1px solid #e5e1ea;
	border-radius: 23px;
	background: #ffffff;
	box-shadow: 0 24px 60px rgba(53, 41, 89, 0.14);
	transform: rotate(-2.4deg);
}

.entry-source::before {
	display: block;
	margin-bottom: 18px;
	color: #918a99;
	content: '客户群';
	font-size: 12px;
	font-weight: 800;
}

.entry-message {
	display: flex;
	align-items: flex-start;
	gap: 9px;
}

.entry-message > i {
	display: grid;
	width: 28px;
	height: 28px;
	flex: 0 0 auto;
	place-items: center;
	border-radius: 50%;
	background: var(--official-orange);
	color: #ffffff;
	font-size: 12px;
	font-style: normal;
}

.entry-message > div {
	padding: 12px 13px;
	border-radius: 6px 15px 15px;
	background: #f2f1f4;
	color: #514b58;
	font-size: 12px;
	line-height: 1.65;
}

.entry-message span {
	display: block;
	margin-bottom: 4px;
	color: #918b98;
}

.entry-message p {
	margin: 0;
}

.entry-placeholder {
	display: grid;
	height: 85px;
	place-items: center;
	margin-top: 12px;
	border: 1px dashed #d8d2df;
	border-radius: 12px;
	background: linear-gradient(135deg, #fbfafc, #f3f0f8);
	color: #9c95a3;
	font-size: 12px;
}

.entry-bridge {
	position: absolute;
	z-index: 7;
	top: 188px;
	left: 39%;
	width: 125px;
	text-align: center;
}

.entry-bridge img {
	display: block;
	width: 112px;
	height: auto;
	margin: 0 auto;
	filter: drop-shadow(0 16px 24px rgba(47, 37, 78, 0.14));
}

.entry-bridge span {
	display: inline-block;
	margin-top: -2px;
	padding: 8px 11px;
	border-radius: 999px;
	background: var(--official-ai-purple);
	box-shadow: 0 10px 23px rgba(112, 64, 224, 0.25);
	color: #ffffff;
	font-size: 12px;
	font-weight: 800;
}

.entry-document {
	position: absolute;
	z-index: 4;
	top: 88px;
	right: 0;
	width: 49%;
	box-sizing: border-box;
	padding: 24px;
	border: 1px solid rgba(112, 64, 224, 0.16);
	border-radius: 11px 25px 25px;
	background: #ffffff;
	box-shadow: 0 27px 68px rgba(53, 41, 89, 0.16);
	transform: rotate(1.5deg);
}

.entry-document-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding-bottom: 14px;
	border-bottom: 1px dashed #ded9e5;
}

.entry-document-head b {
	font-size: 13px;
}

.entry-document-head span {
	padding: 5px 8px;
	border-radius: 999px;
	background: #fff5d8;
	color: #8b6811;
	font-size: 12px;
}

.entry-document-rows {
	display: grid;
	gap: 7px;
	margin-top: 13px;
}

.entry-document-rows div {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 0;
	border-bottom: 1px solid #f0edf3;
}

.entry-document-rows span {
	color: #948e9a;
	font-size: 12px;
}

.entry-document-rows b {
	font-size: 12px;
	text-align: right;
}

.entry-confirm {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-top: 16px;
}

.entry-confirm small {
	color: #928b9a;
	font-size: 12px;
}

.entry-confirm button {
	min-height: 32px;
	padding: 0 12px;
	border: 0;
	border-radius: 8px;
	background: var(--official-green);
	color: #ffffff;
	font-size: 12px;
	font-weight: 700;
	white-space: nowrap;
}

.entry-flow {
	position: absolute;
	z-index: 8;
	right: 2%;
	bottom: 4px;
	left: 2%;
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 8px;
	padding: 13px;
	border: 1px solid rgba(112, 64, 224, 0.12);
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.92);
	box-shadow: 0 14px 36px rgba(53, 41, 89, 0.1);
}

.entry-flow div {
	position: relative;
	padding: 8px 9px;
	border-radius: 9px;
	background: #f7f5fb;
}

.entry-flow div:not(:last-child)::after {
	position: absolute;
	z-index: 2;
	top: 50%;
	right: -8px;
	color: #aaa2b4;
	content: '→';
	transform: translateY(-50%);
}

.entry-flow span,
.entry-flow b {
	display: block;
}

.entry-flow span {
	color: #9a93a1;
	font-size: 12px;
}

.entry-flow b {
	margin-top: 4px;
	font-size: 12px;
}

.plugin-board {
	position: relative;
	min-width: 0;
	padding-top: 44px;
}

.plugin-demo {
	position: relative;
	min-width: 0;
}

.plugin-float {
	position: absolute;
	z-index: 7;
	top: -44px;
	left: -16px;
	display: flex;
	min-width: 320px;
	align-items: center;
	gap: 10px;
	padding: 11px 12px;
	border: 1px solid rgba(112, 64, 224, 0.18);
	border-radius: 15px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 18px 42px rgba(58, 44, 96, 0.16);
	color: #302a3a;
	backdrop-filter: blur(14px);
}

.plugin-float-icon {
	display: grid;
	width: 38px;
	height: 38px;
	flex: 0 0 auto;
	place-items: center;
	border-radius: 11px;
	background: linear-gradient(135deg, var(--official-ai-blue), var(--official-ai-purple));
	box-shadow: 0 8px 18px rgba(112, 64, 224, 0.24);
	color: #ffffff;
}

.plugin-float-icon svg,
.plugin-tab svg,
.plugin-dashboard-head svg {
	width: 21px;
	height: 21px;
	stroke: currentColor;
	stroke-width: 1.8;
	stroke-linecap: round;
	stroke-linejoin: round;
}

.plugin-float-icon svg {
	display: block;
}

.plugin-float p {
	min-width: 0;
	margin: 0;
}

.plugin-float strong {
	font-size: 13px;
	line-height: 1.45;
	white-space: nowrap;
}

.plugin-route {
	position: absolute;
	z-index: 6;
	top: 1px;
	left: 12px;
	width: 90px;
	height: 230px;
	overflow: visible;
	filter: drop-shadow(0 4px 8px rgba(112, 64, 224, 0.12));
	pointer-events: none;
}

.plugin-route path {
	stroke: rgba(112, 64, 224, 0.72);
	stroke-width: 1.7;
	stroke-linecap: round;
	stroke-linejoin: round;
	stroke-dasharray: 5 6;
}

.plugin-app-frame {
	position: relative;
	overflow: hidden;
	aspect-ratio: 2560 / 1680;
	border: 1px solid rgba(112, 64, 224, 0.18);
	border-radius: 22px;
	background: #ffffff;
	box-shadow: 0 28px 68px rgba(58, 44, 96, 0.16);
}

.plugin-app-frame::after {
	position: absolute;
	inset: 0;
	background: linear-gradient(90deg, transparent 0 23%, rgba(245, 244, 250, 0.2) 23% 100%);
	content: '';
	pointer-events: none;
}

.plugin-app-image {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: left center;
}

.plugin-tab {
	position: absolute;
	z-index: 5;
	top: 44%;
	left: -1px;
	display: grid;
	width: 66px;
	height: 62px;
	place-items: center;
	align-content: center;
	gap: 3px;
	border: 1px solid rgba(112, 64, 224, 0.24);
	border-left: 3px solid var(--official-ai-purple);
	border-radius: 0 13px 13px 0;
	background: rgba(246, 242, 255, 0.98);
	box-shadow: 8px 10px 24px rgba(62, 45, 112, 0.16);
	color: var(--official-ai-purple-deep);
}

.plugin-tab span {
	display: grid;
	place-items: center;
}

.plugin-tab svg {
	width: 20px;
	height: 20px;
}

.plugin-tab strong {
	font-size: 10px;
}

.plugin-dashboard {
	position: absolute;
	z-index: 3;
	top: 6.5%;
	right: 3%;
	bottom: 5%;
	left: 29%;
	box-sizing: border-box;
	padding: 18px;
	border: 1px solid #e5e2eb;
	border-radius: 18px;
	background: rgba(255, 255, 255, 0.97);
	box-shadow: 0 18px 42px rgba(46, 37, 76, 0.12);
}

.plugin-dashboard-head,
.plugin-dashboard-head > div {
	display: flex;
	align-items: center;
}

.plugin-dashboard-head {
	justify-content: space-between;
	gap: 10px;
}

.plugin-dashboard-head > div {
	min-width: 0;
	gap: 9px;
}

.plugin-dashboard-head > div > span {
	display: grid;
	width: 34px;
	height: 34px;
	flex: 0 0 auto;
	place-items: center;
	border-radius: 10px;
	background: #f3f0ff;
	color: var(--official-ai-purple-deep);
}

.plugin-dashboard-head p {
	min-width: 0;
	margin: 0;
}

.plugin-dashboard-head strong,
.plugin-dashboard-head em {
	display: block;
}

.plugin-dashboard-head strong {
	font-size: 14px;
}

.plugin-dashboard-head em {
	margin-top: 1px;
	color: #8a8490;
	font-size: 9px;
	font-style: normal;
}

.plugin-dashboard-head > b {
	display: flex;
	align-items: center;
	gap: 5px;
	color: #18795d;
	font-size: 9px;
	white-space: nowrap;
}

.plugin-dashboard-head > b i {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #24a979;
	box-shadow: 0 0 0 4px rgba(36, 169, 121, 0.1);
}

.plugin-dashboard-summary {
	margin-top: 15px;
}

.plugin-dashboard-summary span {
	color: var(--official-ai-purple-deep);
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.05em;
}

.plugin-dashboard-summary strong {
	display: block;
	margin-top: 5px;
	font-size: 18px;
	letter-spacing: -0.02em;
}

.plugin-dashboard-metrics {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 7px;
	margin-top: 13px;
}

.plugin-dashboard-metrics > div {
	min-width: 0;
	padding: 9px 10px;
	border: 1px solid #ece9f0;
	border-radius: 10px;
	background: #faf9fc;
}

.plugin-dashboard-metrics span,
.plugin-dashboard-metrics strong,
.plugin-dashboard-metrics em {
	display: block;
}

.plugin-dashboard-metrics span {
	color: #837d89;
	font-size: 8px;
}

.plugin-dashboard-metrics strong {
	margin-top: 3px;
	color: #168a70;
	font-size: 17px;
}

.plugin-dashboard-metrics em {
	margin-top: 1px;
	color: #96909a;
	font-size: 7px;
	font-style: normal;
}

.plugin-dashboard-lower {
	display: grid;
	grid-template-columns: minmax(0, 1.3fr) minmax(0, 0.7fr);
	gap: 8px;
	margin-top: 9px;
}

.plugin-trend,
.plugin-next-action {
	min-width: 0;
	padding: 10px;
	border: 1px solid #ece9f0;
	border-radius: 10px;
	background: #ffffff;
}

.plugin-trend p {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 6px;
	margin: 0;
}

.plugin-trend p strong {
	font-size: 9px;
}

.plugin-trend p span {
	color: #18866a;
	font-size: 7px;
}

.plugin-trend > div {
	display: flex;
	height: 54px;
	align-items: flex-end;
	gap: 5px;
	margin-top: 8px;
	border-bottom: 1px solid #e7e3ec;
}

.plugin-trend i {
	flex: 1;
	border-radius: 4px 4px 0 0;
	background: #e7e3ee;
}

.plugin-trend i:last-child {
	background: linear-gradient(180deg, var(--official-ai-blue), var(--official-ai-purple));
}

.plugin-next-action {
	background: #f8f5ff;
}

.plugin-next-action span {
	color: var(--official-ai-purple-deep);
	font-size: 8px;
	font-weight: 800;
}

.plugin-next-action strong {
	display: block;
	margin-top: 8px;
	font-size: 11px;
	line-height: 1.4;
}

.plugin-next-action p {
	margin: 6px 0 0;
	color: #77717d;
	font-size: 8px;
	line-height: 1.5;
}

.market-section {
	padding: 104px 0 112px;
	background: linear-gradient(180deg, #f8fbff, #ffffff 48%, #faf7ff);
}

.market-layout {
	display: grid;
	grid-template-columns: 0.88fr 1.12fr;
	gap: 56px;
	align-items: center;
}

.market-intro {
	display: flex;
	min-height: 570px;
	flex-direction: column;
	justify-content: center;
}

.market-intro > p:last-of-type,
.general-intro > p:last-child {
	max-width: 520px;
	margin: 20px 0 0;
	color: var(--official-ink-soft);
	font-size: 16px;
	line-height: 1.8;
}

.market-sign {
	display: flex;
	width: fit-content;
	align-items: center;
	gap: 11px;
	margin-top: 40px;
	padding: 11px 17px 11px 11px;
	border: 1px solid rgba(112, 64, 224, 0.17);
	border-radius: 999px;
	background: #ffffff;
	box-shadow: 0 16px 40px rgba(56, 42, 95, 0.13);
}

.market-sign img {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: #eee8ff;
	object-fit: cover;
	object-position: top;
}

.market-sign b,
.market-sign span {
	display: block;
}

.market-sign b {
	font-size: 12px;
}

.market-sign b em {
	display: inline-block;
	margin-left: 7px;
	padding: 2px 7px;
	border-radius: 999px;
	background: #eee8ff;
	color: var(--official-ai-purple-deep);
	font-size: 10px;
	font-style: normal;
}

.market-sign span {
	margin-top: 2px;
	color: #918b98;
	font-size: 12px;
}

.market-shelf {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 18px;
}

.market-plugin {
	position: relative;
	display: flex;
	min-height: 214px;
	box-sizing: border-box;
	flex-direction: column;
	padding: 24px;
	overflow: hidden;
	border: 1px solid rgba(112, 64, 224, 0.13);
	border-radius: 22px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 21px 55px rgba(56, 42, 95, 0.11);
	transition:
		transform 0.18s ease,
		box-shadow 0.18s ease;
}

.market-plugin:nth-child(1) {
	transform: rotate(-1deg) translateY(7px);
}

.market-plugin:nth-child(2) {
	transform: rotate(0.8deg);
}

.market-plugin:nth-child(3) {
	transform: rotate(0.7deg) translateY(3px);
}

.market-plugin:nth-child(4) {
	transform: rotate(-0.9deg) translateY(-3px);
}

.market-plugin:hover {
	box-shadow: 0 30px 70px rgba(56, 42, 95, 0.17);
	transform: translateY(-8px) rotate(0);
}

.market-plugin::after {
	position: absolute;
	right: -36px;
	bottom: -52px;
	width: 150px;
	height: 150px;
	border-radius: 50%;
	background: radial-gradient(circle, rgba(134, 86, 250, 0.1), transparent 68%);
	content: '';
	pointer-events: none;
}

.market-plugin-head b,
.market-plugin-head small {
	display: block;
}

.market-plugin-head b {
	font-size: 20px;
	line-height: 1.35;
}

.market-plugin-head small {
	margin-top: 7px;
	color: #817989;
	font-size: 12px;
}

.market-plugin > p {
	position: relative;
	z-index: 2;
	max-width: 94%;
	margin: 18px 0 0;
	padding-top: 18px;
	border-top: 1px solid rgba(112, 64, 224, 0.1);
	color: #655e6e;
	font-size: 14px;
	line-height: 1.75;
}

.plugin-status {
	position: absolute;
	z-index: 3;
	bottom: 23px;
	left: 24px;
	color: var(--official-ai-purple-deep);
	font-size: 12px;
	font-weight: 700;
}

.plugin-illustration {
	--illustration-line: #d9dce3;
	--illustration-soft: #f3f4f6;
	--illustration-muted: #bec2ca;
	position: absolute;
	z-index: 2;
	right: 9px;
	bottom: -10px;
	width: 140px;
	height: 100px;
	color: #7856dc;
	opacity: 0.9;
	pointer-events: none;
}

.plugin-illustration svg {
	display: block;
	width: 100%;
	height: 100%;
	overflow: visible;
}

.plugin-illustration .illustration-panel,
.plugin-illustration .illustration-soft {
	fill: var(--illustration-soft);
	stroke: none;
}

.plugin-illustration svg > .illustration-panel:first-child {
	fill: none;
	stroke: none;
}

.plugin-illustration .illustration-line {
	fill: var(--illustration-line);
	stroke: none;
}

.plugin-illustration .illustration-accent {
	fill: currentColor;
	stroke: none;
}

.plugin-illustration .illustration-muted-stroke,
.plugin-illustration .illustration-accent-stroke,
.plugin-illustration .illustration-dash {
	fill: none;
	stroke-linecap: round;
	stroke-linejoin: round;
}

.plugin-illustration .illustration-muted-stroke {
	stroke: var(--illustration-muted);
	stroke-width: 1.8;
}

.plugin-illustration .illustration-accent-stroke {
	stroke: currentColor;
	stroke-width: 2;
}

.plugin-illustration .illustration-dash {
	stroke: var(--illustration-muted);
	stroke-dasharray: 4 4;
	stroke-width: 1.6;
}

.plugin-illustration--customer {
	color: #11aebd;
}

.plugin-illustration--inventory {
	color: #ef7c3c;
}

.plugin-illustration--reconcile {
	color: #20a978;
}

.general-section {
	position: relative;
	overflow: hidden;
	padding: 64px 0 68px;
	background:
		linear-gradient(rgba(92, 64, 185, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(92, 64, 185, 0.035) 1px, transparent 1px), linear-gradient(135deg, #f2fbff, #ffffff 50%, #f8f3ff);
	background-size:
		46px 46px,
		46px 46px,
		auto;
}

.general-layout {
	display: grid;
	grid-template-columns: 0.8fr 1.2fr;
	gap: 44px;
	align-items: center;
}

.ability-map {
	position: relative;
	min-height: 400px;
	isolation: isolate;
}

.ability-map::before {
	position: absolute;
	z-index: -2;
	top: 50%;
	left: 50%;
	width: 286px;
	height: 286px;
	border: 1px dashed rgba(112, 64, 224, 0.15);
	border-radius: 50%;
	box-shadow:
		0 0 0 38px rgba(134, 86, 250, 0.018),
		0 0 0 76px rgba(10, 173, 245, 0.012);
	content: '';
	transform: translate(-50%, -50%);
}

.ability-lines {
	position: absolute;
	z-index: -1;
	inset: 0;
	width: 100%;
	height: 100%;
	overflow: visible;
}

.ability-lines path {
	fill: none;
	stroke-dasharray: 5 8;
	stroke-linecap: round;
	stroke-width: 1.7;
	opacity: 0.6;
}

.ability-lines circle {
	fill: #ffffff;
	stroke: #9570ef;
	stroke-width: 2;
}

.ability-orb {
	position: absolute;
	z-index: 4;
	top: 50%;
	left: 50%;
	display: grid;
	width: 146px;
	height: 146px;
	place-items: end center;
	border: 1px solid rgba(112, 64, 224, 0.18);
	border-radius: 50%;
	background: radial-gradient(circle at 45% 28%, #ffffff 0%, #f4f0ff 42%, #dff5ff 75%, #d7caff 100%);
	box-shadow:
		0 28px 80px rgba(83, 52, 182, 0.24),
		inset 0 0 0 12px rgba(255, 255, 255, 0.5);
	transform: translate(-50%, -50%);
}

.ability-orb img {
	width: 127px;
	height: auto;
	filter: drop-shadow(0 14px 16px rgba(77, 52, 139, 0.18));
}

.ability-orb > span {
	position: absolute;
	z-index: 5;
	bottom: -39px;
	left: 50%;
	width: max-content;
	padding: 10px 16px;
	border: 1px solid rgba(112, 64, 224, 0.13);
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.92);
	box-shadow: 0 12px 30px rgba(56, 42, 95, 0.1);
	color: #59437f;
	font-size: 12px;
	font-weight: 800;
	transform: translateX(-50%);
}

.ability-orb span i {
	display: inline-block;
	width: 7px;
	height: 7px;
	margin-right: 7px;
	border-radius: 50%;
	background: var(--official-green);
	box-shadow: 0 0 0 4px rgba(27, 183, 131, 0.1);
}

.ability-node {
	position: absolute;
	z-index: 5;
	display: flex;
	width: 180px;
	min-height: 72px;
	box-sizing: border-box;
	align-items: center;
	gap: 10px;
	padding: 12px 13px;
	border: 1px solid rgba(112, 64, 224, 0.13);
	border-radius: 19px;
	background: rgba(255, 255, 255, 0.92);
	box-shadow: 0 18px 44px rgba(56, 42, 95, 0.11);
}

.ability-node > i {
	display: grid;
	width: 42px;
	height: 42px;
	flex: 0 0 auto;
	place-items: center;
	border-radius: 13px;
	background: linear-gradient(135deg, var(--official-ai-blue), var(--official-ai-purple));
	box-shadow: 0 10px 22px rgba(93, 64, 193, 0.2);
	color: #ffffff;
	font-size: 14px;
	font-style: normal;
	font-weight: 900;
}

.ability-node h3,
.ability-node p {
	margin: 0;
}

.ability-node h3 {
	font-size: 15px;
}

.ability-node p {
	margin-top: 5px;
	color: #827a89;
	font-size: 12px;
	line-height: 1.5;
}

.system-badges {
	display: flex;
	flex-wrap: wrap;
	gap: 5px;
	margin-top: 7px;
}

.system-badges span {
	padding: 3px 6px;
	border: 1px solid rgba(36, 199, 154, 0.18);
	border-radius: 999px;
	background: rgba(36, 199, 154, 0.08);
	color: #287f67;
	font-size: 10px;
	font-weight: 800;
}

.ability-node--marketing {
	top: 0;
	left: 2%;
	transform: rotate(-1.5deg);
}

.ability-node--image {
	top: 0;
	right: 2%;
	transform: rotate(1.5deg);
}

.ability-node--contract {
	top: 50%;
	left: 0;
	transform: translateY(-50%) rotate(1deg);
}

.ability-node--systems {
	top: 50%;
	right: 0;
	width: 198px;
	transform: translateY(-50%) rotate(-1deg);
}

.ability-node--analysis {
	bottom: 0;
	left: 3%;
	transform: rotate(1.2deg);
}

.ability-node--report {
	right: 3%;
	bottom: 0;
	transform: rotate(-1.2deg);
}

.ability-node--image > i {
	background: linear-gradient(135deg, #ff9c75, #ff5f78);
}

.ability-node--contract > i {
	background: linear-gradient(135deg, #8a68f4, #5d38c7);
}

.ability-node--systems > i {
	background: linear-gradient(135deg, #24c79a, #168a6b);
}

.ability-node--analysis > i {
	background: linear-gradient(135deg, #ffad65, #ff6a2b);
}

.ability-node--report > i {
	background: linear-gradient(135deg, #1bb783, #0aadf5);
}

.permission-stage {
	min-height: 460px;
}

.permission-ticket {
	position: absolute;
	z-index: 4;
	width: 72%;
	box-sizing: border-box;
	padding: 23px;
	border: 1px solid rgba(112, 64, 224, 0.15);
	border-radius: 20px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 25px 62px rgba(56, 42, 95, 0.15);
}

.permission-ticket--top {
	top: 18px;
	right: 0;
	transform: rotate(2deg);
}

.permission-ticket--bottom {
	bottom: 20px;
	left: 0;
	transform: rotate(-2deg);
}

.permission-ticket > span {
	display: inline-flex;
	padding: 6px 9px;
	border-radius: 999px;
	background: #e9f8f2;
	color: #1a8062;
	font-size: 12px;
	font-weight: 800;
}

.permission-ticket--bottom > span {
	background: #eee8ff;
	color: var(--official-ai-purple-deep);
}

.permission-ticket h3 {
	margin: 15px 0 0;
	font-size: 18px;
}

.permission-ticket p {
	margin: 8px 0 0;
	color: #766f7d;
	font-size: 12px;
	line-height: 1.7;
}

.permission-ticket footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 17px;
	padding-top: 13px;
	border-top: 1px solid #ece9f0;
	color: #8d8692;
	font-size: 12px;
}

.permission-ticket footer b {
	color: var(--official-green);
}

.consult-section {
	position: relative;
	overflow: hidden;
	padding: 112px 0 118px;
	background:
		radial-gradient(circle at 12% 30%, rgba(10, 173, 245, 0.12), transparent 28%), radial-gradient(circle at 88% 65%, rgba(134, 86, 250, 0.14), transparent 30%),
		linear-gradient(135deg, #f7fcff 0%, #fbfaff 52%, #f7f2ff 100%);
}

.consult-layout {
	position: relative;
	display: grid;
	grid-template-columns: 0.9fr 1.1fr;
	gap: 76px;
	align-items: center;
}

.consult-copy > span {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 19px;
	color: var(--official-ai-purple-deep);
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.08em;
}

.consult-copy > span::before {
	width: 30px;
	height: 2px;
	border-radius: 99px;
	background: linear-gradient(90deg, var(--official-ai-blue), var(--official-ai-purple));
	content: '';
}

.consult-copy > p {
	max-width: 510px;
	margin: 24px 0 0;
	color: var(--official-ink-soft);
	font-size: 17px;
	line-height: 1.9;
}

.consult-copy .consult-button {
	margin-top: 32px;
}

.consult-scene {
	position: relative;
	min-height: 515px;
}

.consult-scene::before {
	position: absolute;
	inset: 42px 28px 36px 45px;
	border: 1px dashed rgba(112, 64, 224, 0.18);
	border-radius: 50% 45% 48% 42%;
	background: rgba(255, 255, 255, 0.45);
	content: '';
	transform: rotate(-4deg);
}

.consult-question {
	position: absolute;
	z-index: 5;
	top: 32px;
	left: 0;
	width: 275px;
	box-sizing: border-box;
	padding: 20px 22px;
	border: 1px solid rgba(112, 64, 224, 0.14);
	border-radius: 21px 21px 21px 5px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 20px 45px rgba(56, 42, 95, 0.12);
	transform: rotate(-2deg);
}

.consult-question small,
.consult-question b {
	display: block;
}

.consult-question small {
	color: #8c8494;
	font-size: 12px;
}

.consult-question b {
	margin-top: 8px;
	font-size: 17px;
	line-height: 1.55;
}

.consult-avatar {
	position: absolute;
	z-index: 4;
	top: 174px;
	left: 130px;
	display: grid;
	width: 205px;
	height: 205px;
	place-items: end center;
	overflow: hidden;
	border: 1px solid rgba(112, 64, 224, 0.14);
	border-radius: 50%;
	background: radial-gradient(circle at 50% 32%, #ffffff, #eee9ff 58%, #d9f2ff);
	box-shadow:
		0 25px 65px rgba(91, 58, 178, 0.2),
		inset 0 0 0 10px rgba(255, 255, 255, 0.5);
}

.consult-avatar img {
	width: 176px;
	height: auto;
}

.consult-companion {
	position: absolute;
	z-index: 5;
	top: 394px;
	left: 151px;
	padding: 10px 15px;
	border: 1px solid rgba(112, 64, 224, 0.12);
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.94);
	box-shadow: 0 12px 28px rgba(56, 42, 95, 0.1);
	color: #5b4887;
	font-size: 12px;
	font-weight: 800;
}

.consult-companion i {
	display: inline-block;
	width: 7px;
	height: 7px;
	margin-right: 6px;
	border-radius: 50%;
	background: var(--official-green);
	box-shadow: 0 0 0 4px rgba(27, 183, 131, 0.1);
}

.consult-step {
	position: absolute;
	z-index: 6;
	right: 0;
	display: flex;
	width: 280px;
	box-sizing: border-box;
	align-items: center;
	gap: 13px;
	padding: 15px 17px;
	border: 1px solid rgba(112, 64, 224, 0.12);
	border-radius: 17px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 17px 38px rgba(56, 42, 95, 0.11);
}

.consult-step > i {
	display: grid;
	width: 37px;
	height: 37px;
	flex: 0 0 auto;
	place-items: center;
	border-radius: 12px;
	background: linear-gradient(135deg, var(--official-ai-blue), var(--official-ai-purple));
	color: #ffffff;
	font-size: 12px;
	font-style: normal;
	font-weight: 900;
}

.consult-step b,
.consult-step small {
	display: block;
}

.consult-step b {
	font-size: 12px;
}

.consult-step small {
	margin-top: 4px;
	color: #918a99;
	font-size: 12px;
	white-space: nowrap;
}

.consult-step--one {
	top: 93px;
	transform: rotate(2deg);
}

.consult-step--two {
	top: 217px;
	right: -22px;
	transform: rotate(-1deg);
}

.consult-step--three {
	top: 342px;
	right: 10px;
	transform: rotate(1.5deg);
}

.cta-section {
	padding: 105px 0;
}

.cta-card {
	position: relative;
	display: grid;
	min-height: 0;
	grid-template-columns: 1fr auto;
	align-items: center;
	gap: 50px;
	padding: 60px 64px;
	overflow: visible;
	border-radius: 28px;
	background: radial-gradient(circle at 90% 10%, rgba(76, 204, 255, 0.28), transparent 28%), linear-gradient(110deg, #5130be, #8662f4 55%, #25b6f4);
	box-shadow: 0 30px 70px rgba(83, 52, 182, 0.24);
	color: #ffffff;
}

.cta-copy {
	position: relative;
	z-index: 2;
	max-width: 690px;
}

.cta-copy h2 {
	margin: 0;
	font-size: 40px;
	font-weight: 900;
	letter-spacing: -0.045em;
	line-height: 1.2;
}

.cta-copy p {
	margin: 13px 0 0;
	color: rgba(255, 255, 255, 0.75);
	font-size: 14px;
}

.cta-card > img {
	position: absolute;
	right: 215px;
	bottom: 0;
	width: 340px;
	max-height: 100%;
	height: auto;
	margin: 0;
	opacity: 0.82;
	object-fit: contain;
	pointer-events: none;
	mix-blend-mode: screen;
}

.cta-download {
	position: relative;
	z-index: 2;
}

.cta-download-button {
	display: inline-flex;
	min-height: 52px;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 0 23px;
	border: 0;
	border-radius: 13px;
	background: #ffffff;
	box-shadow: 0 14px 30px rgba(54, 34, 121, 0.2);
	color: var(--official-ai-purple-deep);
	font-size: 16px;
	font-weight: 800;
	cursor: pointer;
}

.consult-mask {
	position: fixed;
	z-index: 3000;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(19, 22, 48, 0.68);
	backdrop-filter: blur(4px);
}

.consult-dialog {
	position: relative;
	width: 410px;
	padding: 32px 36px 36px;
	border-radius: 22px;
	background: #ffffff;
	box-shadow: 0 28px 90px rgba(0, 0, 0, 0.3);
	text-align: center;
}

.dialog-close {
	position: absolute;
	top: 16px;
	right: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	border: 0;
	border-radius: 50%;
	background: #f1f3f8;
	color: #687083;
	cursor: pointer;
}

.dialog-brand {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 12px 6px 7px;
	border-radius: 999px;
	background: linear-gradient(135deg, rgba($aiBlue, 0.12), rgba($aiPurple, 0.14));
	color: $aiPurple;
	font-size: 12px;
	font-weight: 600;
}

.dialog-brand img {
	width: 26px;
	height: 26px;
	border-radius: 8px;
}

.consult-dialog h2 {
	margin: 20px 0 9px;
	color: #22283a;
	font-size: 25px;
}

.consult-dialog > p {
	margin: 0;
	color: #767e90;
	font-size: 14px;
	line-height: 1.7;
}

.qrcode-frame {
	width: 230px;
	height: 230px;
	margin: 22px auto 14px;
	padding: 10px;
	border: 1px solid #e2e5ef;
	border-radius: 16px;
	background: #ffffff;
}

.qrcode-frame img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: contain;
}

.dialog-tip {
	color: #969dac;
	font-size: 12px;
}

@media (prefers-reduced-motion: reduce) {
	.ai-assistant-page *,
	.ai-assistant-page *::before,
	.ai-assistant-page *::after {
		animation-duration: 0.01ms !important;
		transition-duration: 0.01ms !important;
	}
}

@media (max-width: 1280px) {
	.section-shell {
		width: calc(100% - 56px);
	}

	.hero-grid {
		grid-template-columns: minmax(340px, 0.9fr) minmax(460px, 1.1fr);
		gap: 36px;
	}

	.hero-copy h1 {
		font-size: 61px;
	}

	.hero-description {
		font-size: 16px;
	}

	.official-grid,
	.consult-layout {
		gap: 48px;
	}
}

@media (max-width: 1180px) {
	.plugin-dashboard {
		left: 27%;
		padding: 14px;
	}

	.plugin-dashboard-summary {
		margin-top: 10px;
	}

	.plugin-dashboard-summary strong {
		font-size: 15px;
	}

	.plugin-dashboard-metrics {
		margin-top: 9px;
	}

	.plugin-dashboard-lower {
		margin-top: 7px;
	}

	.plugin-trend > div {
		height: 45px;
	}
}

@media (max-width: 980px) {
	.section-shell {
		width: calc(100% - 40px);
		max-width: 780px;
	}

	.hero-grid,
	.official-grid,
	.market-layout,
	.general-layout,
	.consult-layout {
		grid-template-columns: 1fr;
		gap: 50px;
	}

	.hero-section {
		min-height: auto;
		padding: 72px 0 82px;
	}

	.official-section {
		padding: 92px 0;
	}

	.market-intro {
		min-height: 0;
		max-width: 650px;
	}

	.market-sign {
		margin-top: 28px;
	}

	.general-intro,
	.consult-copy {
		max-width: 650px;
	}

	.ability-map {
		width: 100%;
		max-width: 700px;
		min-height: 440px;
		margin: 0 auto;
	}

	.consult-scene {
		width: 100%;
		max-width: 630px;
		margin: 0 auto;
	}

	.cta-card {
		padding: 48px;
	}

	.cta-card > img {
		right: 60px;
		width: 310px;
		opacity: 0.5;
	}
}

@media (max-width: 767px) {
	.plugin-board {
		padding-top: 42px;
	}

	.plugin-float {
		top: -42px;
		right: 7px;
		left: 7px;
		min-width: 0;
		padding: 9px 10px;
	}

	.plugin-float-icon {
		width: 34px;
		height: 34px;
	}

	.plugin-float strong {
		font-size: 12px;
	}

	.plugin-route {
		top: 0;
		left: 15px;
		width: 74px;
		height: 190px;
	}

	.plugin-app-frame {
		aspect-ratio: 1.12;
		border-radius: 18px;
	}

	.plugin-tab {
		top: 41%;
		width: 49px;
		height: 54px;
		border-radius: 0 11px 11px 0;
	}

	.plugin-tab svg {
		width: 17px;
		height: 17px;
	}

	.plugin-tab strong {
		font-size: 9px;
	}

	.plugin-dashboard {
		top: 5%;
		right: 3%;
		bottom: 5%;
		left: 24%;
		padding: 11px;
		border-radius: 14px;
	}

	.plugin-dashboard-head > div {
		gap: 6px;
	}

	.plugin-dashboard-head > div > span {
		width: 29px;
		height: 29px;
		border-radius: 8px;
	}

	.plugin-dashboard-head svg {
		width: 17px;
		height: 17px;
	}

	.plugin-dashboard-head strong {
		font-size: 12px;
	}

	.plugin-dashboard-head em,
	.plugin-dashboard-head > b {
		font-size: 8px;
	}

	.plugin-dashboard-summary {
		margin-top: 9px;
	}

	.plugin-dashboard-summary span {
		font-size: 8px;
	}

	.plugin-dashboard-summary strong {
		margin-top: 3px;
		font-size: 14px;
		line-height: 1.35;
	}

	.plugin-dashboard-metrics {
		gap: 4px;
		margin-top: 8px;
	}

	.plugin-dashboard-metrics > div {
		padding: 7px 6px;
		border-radius: 8px;
	}

	.plugin-dashboard-metrics span {
		font-size: 7px;
	}

	.plugin-dashboard-metrics strong {
		font-size: 13px;
	}

	.plugin-dashboard-metrics em {
		font-size: 6px;
	}

	.plugin-dashboard-lower {
		grid-template-columns: 1fr;
		margin-top: 7px;
	}

	.plugin-trend {
		padding: 8px;
	}

	.plugin-trend p strong {
		font-size: 8px;
	}

	.plugin-trend > div {
		height: 59px;
		margin-top: 6px;
	}

	.plugin-next-action {
		display: none;
	}
}

@media (max-width: 720px) {
	.section-shell {
		width: calc(100% - 32px);
	}

	.hero-section {
		padding: 48px 0 64px;
	}

	.hero-grid {
		gap: 58px;
	}

	.hero-copy h1 {
		font-size: 40px;
	}

	.hero-description br {
		display: none;
	}

	.orbit-label {
		top: -17px;
		right: 10px;
	}

	.xiaoguo-card {
		right: 6px;
		bottom: -34px;
		left: auto;
	}

	.official-section {
		padding: 74px 0;
	}

	.section-copy h2,
	.market-intro h2,
	.general-intro h2 {
		font-size: 36px;
	}

	.section-desc,
	.market-intro > p:last-of-type,
	.general-intro > p:last-child {
		font-size: 15px;
	}

	.query-scene {
		min-height: 700px;
	}

	.query-question {
		top: 0;
		right: 24px;
		left: 0;
		max-width: none;
		transform: none;
	}

	.query-report {
		top: 102px;
		right: 0;
		left: 9px;
		width: auto;
		padding: 19px;
		transform: none;
	}

	.query-report-grid {
		grid-template-columns: 1fr;
	}

	.query-report-grid div {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
	}

	.query-report-grid strong,
	.query-report-grid small {
		margin: 0;
		text-align: right;
	}

	.query-xiaoguo {
		bottom: 10px;
		left: -4px;
		width: 120px;
	}

	.query-caption {
		bottom: 32px;
		left: 93px;
	}

	.query-clue {
		display: none;
	}

	.entry-stage {
		min-height: 820px;
	}

	.entry-tabs {
		top: 0;
		width: 100%;
		max-width: 260px;
		box-sizing: border-box;
	}

	.entry-tabs button {
		min-width: 0;
		min-height: 40px;
		flex: 1;
	}

	.entry-source {
		top: 80px;
		left: 4px;
		width: calc(100% - 8px);
		transform: none;
	}

	.entry-bridge {
		top: 318px;
		left: 50%;
		transform: translateX(-50%);
	}

	.entry-document {
		top: 492px;
		right: 4px;
		left: 4px;
		width: auto;
		transform: none;
	}

	.entry-flow {
		display: none;
	}

	.entry-confirm {
		align-items: stretch;
		flex-direction: column;
	}

	.entry-confirm button {
		min-height: 40px;
	}

	.market-shelf {
		grid-template-columns: 1fr;
	}

	.market-section {
		padding: 78px 0;
	}

	.market-plugin,
	.market-plugin:nth-child(1),
	.market-plugin:nth-child(2),
	.market-plugin:nth-child(3),
	.market-plugin:nth-child(4) {
		min-height: 238px;
		transform: none;
	}

	.general-section {
		padding: 66px 0;
	}

	.ability-map {
		display: grid;
		min-height: auto;
		grid-template-columns: 1fr 1fr;
		gap: 12px;
		margin-top: 32px;
	}

	.ability-map::before {
		top: 75px;
		width: 230px;
		height: 230px;
		box-shadow: none;
	}

	.ability-lines {
		display: none;
	}

	.ability-orb {
		position: relative;
		top: auto;
		left: auto;
		grid-column: 1 / -1;
		width: 150px;
		height: 150px;
		margin: 0 auto 52px;
		transform: none;
	}

	.ability-node,
	.ability-node--marketing,
	.ability-node--image,
	.ability-node--contract,
	.ability-node--systems,
	.ability-node--analysis,
	.ability-node--report {
		position: relative;
		top: auto;
		right: auto;
		bottom: auto;
		left: auto;
		width: 100%;
		min-height: 108px;
		box-sizing: border-box;
		align-items: flex-start;
		flex-direction: column;
		gap: 8px;
		transform: none;
	}

	.permission-stage {
		display: grid;
		min-height: auto;
		gap: 16px;
	}

	.permission-ticket,
	.permission-ticket--top,
	.permission-ticket--bottom {
		position: relative;
		top: auto;
		right: auto;
		bottom: auto;
		left: auto;
		width: 100%;
		transform: none;
	}

	.consult-section {
		padding: 76px 0 82px;
	}

	.consult-copy h2 {
		font-size: 37px;
	}

	.consult-copy .consult-button {
		width: 100%;
	}

	.consult-scene {
		display: grid;
		min-height: auto;
		gap: 13px;
	}

	.consult-scene::before {
		inset: 0;
		border-radius: 25px;
	}

	.consult-question,
	.consult-avatar,
	.consult-companion,
	.consult-step,
	.consult-step--one,
	.consult-step--two,
	.consult-step--three {
		position: relative;
		top: auto;
		right: auto;
		left: auto;
		width: 100%;
		box-sizing: border-box;
		transform: none;
	}

	.consult-question {
		order: 1;
	}

	.consult-avatar {
		order: 2;
		width: 150px;
		height: 150px;
		margin: 8px auto 0;
	}

	.consult-avatar img {
		width: 130px;
	}

	.consult-companion {
		order: 3;
		width: max-content;
		margin: -5px auto 7px;
	}

	.consult-step {
		order: 4;
	}

	.consult-step--two {
		order: 5;
	}

	.consult-step--three {
		order: 6;
	}

	.cta-card {
		min-height: 390px;
		grid-template-columns: 1fr;
		align-content: space-between;
		padding: 40px 25px;
	}

	.cta-copy h2 {
		font-size: 33px;
	}

	.cta-card > img {
		right: -70px;
		width: 300px;
		opacity: 0.28;
	}

	.cta-download-button {
		box-sizing: border-box;
		width: 100%;
	}

	.consult-dialog {
		width: 100%;
		max-width: 410px;
		box-sizing: border-box;
	}
}
</style>
